import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { color, FormatUtils, useAuthedRequestFn } from '@my/util'
import { YStack, FinancialTable, XStack, SimpleText, Spinner, useMedia, ScrollView } from '@my/ui'
import { FinancialReviewHeading, ExportDropdown } from '../../../components/index'
import { CashFlowAnalysis, CashFlowTimePeriods } from './cashFlowAnalysis/CashFlowAnalysis'
import { buildAvailableCashData, buildDataFromAPI, valueToMonthData } from '../helpers'
import { TipCard } from './tipCard/TipCard'
import images from '../../../assets/images'
import { Platform } from 'react-native'

// Header for the Cash Flow Analysis Section with export functionality
const FinancialGoalTableHeader = ({ onExport }) => (
  <XStack
    flexWrap="wrap"
    justifyContent="space-between"
    width="100%"
    alignItems="center"
    marginVertical={10}
  >
    <SimpleText fontSize={14} fontWeight="800" color={color.grey} lineHeight={17} p={3}>
      FORECAST BREAKDOWN
    </SimpleText>
    {/* <YStack>
      <ExportDropdown onExport={onExport} />
    </YStack> */}
  </XStack>
)

// Container wrapping the financial review heading and child components
const FinancialContainer = ({ children }) => (
  <YStack p="$4" gap={25} bg={color.white} alignItems='center'>
    <FinancialReviewHeading
      title="Savings Forecast"
      img={images.cashFlow}
      imageStyle={{
        //'$platform-web': { height: 22, width: 27 },
        //'$platform-native': { height: 21, width: 25 },
      }}
    />
    {children}
  </YStack>
)

// Custom hook to fetch the financial review data
const useFinancialReviewData = (authedRequestFn) => {
  return useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })
}

// Main component for rendering the Financial Cash Flow view
export const FinancialCashFlow = () => {
  const authedRequestFn = useAuthedRequestFn()
  const [activePeriod, setActivePeriod] = useState<CashFlowTimePeriods>('6 Months')

  // Fetching financial review data (this must always happen)
  const reviewDataQuery = useFinancialReviewData(authedRequestFn)
  const media = useMedia();
  const { isLoading, data, isError } = reviewDataQuery

  // Consistent fallback if data is unavailable
  const { barChartUI = [], tableUI = [] } = data?.cashFlowAnalysisUI || {}
  const { tipToImproveCashFlow = '' } = data?.recommendedBudget || {}
  const cashFlowAnalysis = data?.cashFlowAnalysis || [];


  // Ensure `useMemo` is not conditionally executed
  const filteredCashFlowData = useMemo(() => {
    const monthsMap = {
      //'1 Month': 1,
      '3 Months': 3,
      '6 Months': 6,
      '9 Months': 9,
    }
    const monthsToShow = monthsMap[activePeriod] || 6 // Default to 6 months if no valid period is selected
    return cashFlowAnalysis.slice(0, monthsToShow) // Get the last N months based on selected period
  }, [cashFlowAnalysis, activePeriod])

  // Processing raw data for the financial table
  const updatedData = useMemo(() => (cashFlowAnalysis ? buildDataFromAPI(cashFlowAnalysis) : []), [cashFlowAnalysis]);
  const availableCashData = useMemo(
    () => (cashFlowAnalysis ? buildAvailableCashData(cashFlowAnalysis) : []),
    [cashFlowAnalysis]
  );
  const mobileData = useMemo(
    () => (cashFlowAnalysis ? cashFlowAnalysis.map((item) => {
      const newItem = {...item};
      newItem.month = FormatUtils.dateTime['Month yyyy'](item?.month+"-02");
      ['monthlyIncome', 'spendingThisMonth', 'totalSpendingToDate', 'availableCash'].map(key => {
        newItem[key] = FormatUtils.currency.cashFormat(item[key]);
      })
      return newItem;
    }) : []),
    [cashFlowAnalysis]
  );

  const handleExport = (format) => {
    console.log(`Export as ${format}`)
    // Replace with actual export logic
  }

  const content = isLoading
  ? <Spinner size="large" />
  : isError
    ? <SimpleText>Error fetching financial data!</SimpleText>
    : <>
      {/* Cash Flow Analysis Section */}
      <CashFlowAnalysis
        data={filteredCashFlowData}
        activePeriod={activePeriod}
        onPeriodChange={setActivePeriod}
      />
      {/* Financial Goal Table Header with export option */}
      <FinancialGoalTableHeader onExport={handleExport} />
      {/* Financial Table displaying the financial data */}
      {media['max-sm'] 
        ? <FinancialTable
          data={mobileData}
          headings={[
            { key: 'month', text: 'Month' },
            { key: 'monthlyIncome', text: 'Monthly Income' },
            { key: 'spendingThisMonth', text: 'Forecasted Spending' },
            { key: 'totalSpendingToDate', text: 'Accumulated Spending' },
            { key: 'availableCash', text: 'Forecasted Balance' },
            ,
          ]}
          
            mobileRenderOptions={{
              titleKey: 'month',
              //mainValueKeys: ['currentAmount', 'recommendedAmount'],
              //interactiveKeys: ['actionButtons'],
              otherKeys: [['spendingThisMonth','monthlyIncome'], ['totalSpendingToDate', 'availableCash']]
            }}
        />
        : <FinancialTable
          data={updatedData}
          headings={[
            { key: 'Assumptions', text: 'Overview' },
            ...cashFlowAnalysis.map((item) => ({ key: item?.month, text: FormatUtils.dateTime['Month yyyy'](item?.month+"-02") })),
          ]}
          items={availableCashData}
        />
      }
      {/* Tip Card for improving cash flow */}
      <TipCard title="Track and Reduce Unnecessary Spending" content={tipToImproveCashFlow} />
    </>

  const output = <FinancialContainer>{content}</FinancialContainer>;
  
  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}
